import Polyglot from "node-polyglot";

import mergeDeep from "utils/mergeDeep";

import getLocale, { getFallbackLocale } from "./locale";

const translations = require("../../locales/translations.js"); // eslint-disable-line

const getAssetsLink = (portfolioID, assets) =>
  assets
    .split(",")
    .map(
      (asset) =>
        `<a href='/assets?portfolioIDs[]=${portfolioID}&keywords=${asset.trim()}'>${asset.trim()}</a>`
    )
    .join(", ");

export default class Translate {
  constructor(component, locale = null) {
    this.component = component;
    this.userLocale = locale;
    this.initialize = this.initialize.bind(this);
    this.t = this.t.bind(this);
    this.initialize();
  }

  initialize() {
    this.locale = this.userLocale || getLocale();
    this.fallbackLocale = getFallbackLocale(this.locale);

    this.polyglot = new Polyglot({
      phrases: mergeDeep(this.getSharedTranslations(), this.getComponentTranslations()),
      allowMissing: process.env.NODE_ENV !== "development",
    });
  }

  t(key, params) {
    const p = params || 1;
    if (!this.userLocale && this.locale !== getLocale()) this.initialize();

    if (
      key &&
      (key.toLowerCase().endsWith("spaces.other") ||
        key.toLowerCase().endsWith("categories.other") ||
        key.toLowerCase().endsWith("industries.other"))
    ) {
      return this.polyglot.t(`${key}_cat`, p);
    }

    return this.polyglot.t(key, p);
  }

  getComponentTranslations() {
    if (this.locale === this.fallbackLocale || !translations[this.locale][this.component]) {
      return translations[this.fallbackLocale][this.component];
    }

    return mergeDeep(
      translations[this.fallbackLocale][this.component],
      translations[this.locale][this.component]
    );
  }

  getSharedTranslations() {
    if (this.locale === this.fallbackLocale || !translations[this.locale].shared) {
      return translations[this.fallbackLocale].shared;
    }

    return mergeDeep(translations[this.fallbackLocale].shared, translations[this.locale].shared);
  }
}

export class SystemEventTranslate {
  constructor() {
    this.locale = getLocale();
    this.fallbackLocale = getFallbackLocale(this.locale);
    this.initialize = this.initialize.bind(this);
    this.t = this.t.bind(this);
    this.initialize();
  }

  initialize() {
    this.polyglot = new Polyglot({
      phrases: this.getSharedTranslations(),
      allowMissing: process.env.NODE_ENV !== "development",
    });
  }

  getSharedTranslations() {
    if (this.locale === this.fallbackLocale || !translations[this.locale].shared) {
      return translations[this.fallbackLocale].shared;
    }

    return mergeDeep(translations[this.fallbackLocale].shared, translations[this.locale].shared);
  }

  t(action, content, isLinked = false) {
    const creator = content.creator.deleted
      ? `<em>${this.polyglot.t("deletedUser")}</em>`
      : `${content.creator.fname} ${content.creator.lname}`;
    const ancestorID = content.deal && content.deal.ancestor && content.deal.ancestor.id;
    const portfolioID = content.deal && content.deal.portfolio.id;

    const params = {
      actor: creator,
      tenant: content.after,
      asset: isLinked ? content.after && getAssetsLink(portfolioID, content.after) : content.after,
      ancestor: isLinked ? `<a href='/deals/${ancestorID}'>${content.after}</a>` : content.after,
      ...content,
      creator,
    };

    if (action === "deal_status_update") {
      params.before = this.polyglot.t(`dealStatuses.${content.before}`);
      params.after = this.polyglot.t(`dealStatuses.${content.after}`);
    } else if (action === "deal_stage_update") {
      params.before = this.polyglot.t(`dealStages.${content.before}`);
      params.after = this.polyglot.t(`dealStages.${content.after}`);
    } else if (action === "deal_share_status_update") {
      params.after = this.polyglot.t(`shareStatuses.${content.after}`, {
        actor: content.actor,
      });
    } else if (action === "deal_remove_asset") {
      params.asset = content.before || "";
    } else if (action === "deal_channel_add") {
      params.channel = content.after || "";
    } else if (action === "deal_accept_proposal") {
      params.name = content.after;
    } else if (action === "asset_lead_stage_update") {
      params.before = this.polyglot.t(`assetLeadStages.${content.before}`);
      params.after = this.polyglot.t(`assetLeadStages.${content.after}`);
    } else if (action === "asset_lead_add_contact") {
      params.contact = content.after || "";
    } else if (action === "asset_lead_status_update") {
      params.before = this.polyglot.t(`dealStatuses.${content.before}`);
      params.after = this.polyglot.t(`dealStatuses.${content.after}`);
    }

    return this.polyglot.t(`systemEvents.${action}`, params);
  }
}
